import { useRouter } from 'next/router';
import { PropsWithChildren, useEffect, useReducer } from 'react';
import GrispiLoadingLayout from '../layouts/GrispiLoadingLayout';
import { tenantIdExistsService } from '../services/auth/tenant';
import { getTenantId } from '../utils/accountHelper';
import { nonNullishValue, nullishValue } from '../utils/helpers';
import { isTenantIdRequiredPage, redirectTo } from '../utils/routeHelper';

type State = { isTenantExisting: boolean; shouldCheckTenant: boolean; isTenantCheckDone: boolean };

type Action =
    | { type: 'startTenantCheck' }
    | { type: 'finishTenantCheck'; value: boolean }
    | { type: 'errorOnTenantCheck' }
    | { type: 'resetState' };

const initialState = { isTenantExisting: undefined, shouldCheckTenant: undefined, isTenantCheckDone: undefined };

function tenantControlReducer(state: State, action: Action): State {
    switch (action.type) {
        case 'startTenantCheck':
            return { ...state, shouldCheckTenant: true, isTenantCheckDone: false };
        case 'finishTenantCheck':
            return { isTenantExisting: action.value, shouldCheckTenant: false, isTenantCheckDone: true };
        case 'errorOnTenantCheck':
            return { ...state, shouldCheckTenant: false, isTenantCheckDone: false };
        case 'resetState':
            return initialState;
        default:
            throw new Error('action type not supported');
    }
}

/**
 * If the page requires a tenant-id, check if the tenant-id exists,
 *      if it doesn't, redirect to 404
 *      if the check fails, redirect to 500
 * @returns The component is returning the children if the page does not require a tenant-id or if the tenant-id exists. Otherwise, it returns a loading layout.
 */
export default function TenantAvailabilityController({ children }: PropsWithChildren) {
    const { pathname } = useRouter();
    const [state, dispatch] = useReducer(tenantControlReducer, initialState);

    const initTenantCheck = isTenantIdRequiredPage(pathname) && nullishValue(state.isTenantExisting);
    useEffect(() => {
        initTenantCheck && dispatch({ type: 'startTenantCheck' });
    }, [initTenantCheck]);

    useEffect(() => {
        if (state.shouldCheckTenant) {
            tenantIdExistsService(getTenantId())
                .then((response) => {
                    dispatch({ type: 'finishTenantCheck', value: response });
                })
                .catch(() => {
                    dispatch({ type: 'errorOnTenantCheck' });
                });
        }
    }, [state.shouldCheckTenant]);

    const isRedirectNeeded =
        nonNullishValue(state.isTenantCheckDone) &&
        state.isTenantCheckDone &&
        nonNullishValue(state.isTenantExisting) &&
        !state.isTenantExisting;

    useEffect(() => {
        isRedirectNeeded && redirectTo('/404');
    }, [isRedirectNeeded]);

    const isRedirectOnErrorNeeded =
        nullishValue(state.isTenantExisting) &&
        nonNullishValue(state.isTenantCheckDone) &&
        !state.isTenantCheckDone &&
        nonNullishValue(state.shouldCheckTenant) &&
        !state.shouldCheckTenant;

    useEffect(() => {
        isRedirectOnErrorNeeded && redirectTo('/500');
    }, [isRedirectOnErrorNeeded]);

    if (!isTenantIdRequiredPage(pathname) || state.isTenantExisting) return <>{children}</>;

    return <GrispiLoadingLayout />;
}
